import { Link } from "react-router-dom";
import {
  Car, Caravan, Truck, Tractor, Ship, Bike, Gauge, Fuel, Cog,
  Headset, Timer, Cpu, ArrowRight, CheckCircle2,
} from "lucide-react";
import { MaskedLines, Reveal, SectionHead, PortalButton } from "@/components/Motion";

const VEHICLES = [
  [Car, "Cars", "Petrol & diesel, Stage 1–3, pops & bangs, speed limiters"],
  [Caravan, "Vans / LCV", "Torque & economy calibrations for working vehicles"],
  [Truck, "HGV / Commercial", "Fleet economy, power and AdBlue solutions"],
  [Tractor, "Agricultural", "Tractors & harvesters — power for the working season"],
  [Ship, "Marine", "Inboard & outboard engine calibration"],
  [Bike, "Motorcycles", "Road & track bike ECU tuning"],
];

const ECUS = ["Bosch EDC / ME / MG / MD", "Delphi / DCM", "Siemens / Continental", "Magneti Marelli", "Valeo", "Denso", "Hitachi", "Delco", "Cummins / Caterpillar", "Temic / ZF TCU", "Getrag / VAG DSG", "Tricore & locked ECUs"];

const SERVICES = [
  [Gauge, "Performance Tuning", "Stage 1, 2 and 3 calibrations developed on our 4WD dyno — safe, smooth and verified power gains."],
  [Fuel, "Economy Tuning", "Fleet-proven economy calibrations reducing fuel consumption on vans, HGVs and agricultural machinery."],
  [Cog, "Gearbox / TCU Tuning", "DSG, ZF and automatic gearbox calibrations — shift speed, torque limiters and launch control."],
  [Cpu, "Solution Files", "DPF, EGR, AdBlue, DTC and swirl flap solutions for off-road and motorsport applications."],
];

const HOW = [
  ["01", "Read the ECU", "Use your Master or Slave tool to read the original file via OBD, Bench or Boot."],
  ["02", "Upload to the portal", "Submit the file through the Regal File Portal with your vehicle and job details."],
  ["03", "We calibrate", "An experienced calibrator modifies your file — dyno-developed strategies, not generic maps."],
  ["04", "Download & flash", "Receive your tuned file, flash it back and log the results. Support if you need it."],
];

export default function TuningFiles() {
  return (
    <main data-testid="tuning-files-page">
      <section className="relative overflow-hidden pt-36 pb-20 sm:pt-44 sm:pb-28">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-void via-transparent to-void" />
        <div className="container-regal relative">
          <MaskedLines
            lines={["Professional ECU", "Tuning File Service"]}
            className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[1.0] tracking-tight max-w-4xl"
          />
          <Reveal delay={0.5} className="mt-7 max-w-2xl">
            <p className="text-base sm:text-lg text-neutral-300 leading-relaxed">
              Professional ECU calibration developed and supplied by experienced tuners. Upload your original file, and receive a custom calibration built by people who tune cars on a dyno every day.
            </p>
          </Reveal>
          <Reveal delay={0.62} className="mt-9 flex flex-wrap gap-4">
            <PortalButton label="Upload a File" testId="files-hero-upload-button" className="px-9 py-4" />
            <Link to="/become-a-dealer" data-testid="files-dealer-link" className="inline-flex items-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-neutral-100 hover:border-regal-amber hover:text-regal-amber transition-colors">
              Become a Dealer <ArrowRight size={15} />
            </Link>
          </Reveal>
          <Reveal delay={0.72} className="mt-10 flex flex-wrap gap-x-10 gap-y-4 border-t border-line pt-7">
            {[[Timer, "15–60 min turnaround"], [Gauge, "4WD dyno developed"], [Headset, "Technical support desk"]].map(([Icon, t]) => (
              <span key={t} className="flex items-center gap-2.5 text-sm text-neutral-300"><Icon size={16} className="text-regal-amber" /> {t}</span>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="border-b border-line" data-testid="dyno-showcase-section">
        <Reveal y={0}>
          <div className="relative overflow-hidden">
            <img src="/assets/workshop/dyno-911-blue2.webp" alt="Porsche 911 Turbo on the Regal Tuning 4WD dyno" loading="lazy" className="aspect-[4/3] w-full object-cover object-[center_55%] sm:aspect-[21/9]" />
            <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-transparent to-transparent" />
            <p className="absolute bottom-5 left-5 font-mono text-[11px] uppercase tracking-[0.24em] text-neutral-300 sm:left-12">Regal Tuning — In-house 4WD dyno cell</p>
          </div>
        </Reveal>
      </section>

      <section className="border-t border-line py-20 sm:py-24" data-testid="vehicle-types-section">
        <div className="container-regal">
          <SectionHead overline="Coverage" title="Supported vehicle types" copy="One file service covering virtually every ECU-equipped vehicle." />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {VEHICLES.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div className="flex h-full gap-5 border border-line bg-card p-6 hover:border-regal-amber/50 transition-colors duration-300">
                  <Icon size={26} className="shrink-0 text-regal-amber" strokeWidth={1.5} />
                  <div>
                    <p className="font-display text-base font-bold uppercase tracking-wide">{t}</p>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-400">{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="ecu-types-section">
        <div className="container-regal">
          <SectionHead overline="Compatibility" title="Supported ECU types" copy="We calibrate files from all major ECU and TCU families, including the latest locked and Tricore-based units." />
          <div className="flex flex-wrap gap-3">
            {ECUS.map((e, i) => (
              <Reveal key={e} delay={i * 0.03}>
                <span className="flex items-center gap-2 border border-line bg-card px-4 py-2.5 font-mono text-xs text-neutral-300">
                  <CheckCircle2 size={13} className="text-regal-amber" /> {e}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line py-20 sm:py-24" data-testid="services-section">
        <div className="container-regal">
          <SectionHead overline="Calibration Services" title="Performance. Economy. Gearbox." />
          <div className="grid gap-px border border-line bg-line sm:grid-cols-2">
            {SERVICES.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 0.07} className="bg-void">
                <div className="h-full p-8">
                  <Icon size={24} className="text-regal-amber" strokeWidth={1.5} />
                  <p className="mt-5 font-display text-lg font-bold uppercase tracking-wide">{t}</p>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-400">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="how-it-works-section">
        <div className="container-regal">
          <SectionHead overline="Process" title="How the file service works" />
          <div className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
            {HOW.map(([n, t, d], i) => (
              <Reveal key={n} delay={i * 0.08} className="bg-surface">
                <div className="h-full p-7" data-testid={`how-step-${i + 1}`}>
                  <p className="font-mono text-xs tracking-[0.3em] text-regal-amber">{n}</p>
                  <p className="mt-4 font-display text-base font-bold uppercase tracking-wide">{t}</p>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-400">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-12 text-center">
            <PortalButton label="Upload a File" testId="files-bottom-upload-button" className="px-12 py-5 text-base" />
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">File uploads are handled by the existing Regal Tuning portal</p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
